import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Edit, Lock } from 'lucide-react';
import type { Pet } from '../types/pet';
import { PhysicalMetrics } from './records/PhysicalMetrics';
import { VaccinationRecords } from './records/VaccinationRecords';
import { GroomingRecords } from './records/GroomingRecords';
import { MedicalUpdates } from './records/MedicalUpdates';
import { DocumentTimeline } from './records/DocumentTimeline';
import { InsuranceRecords } from './records/InsuranceRecords';

interface HealthCareRecordsProps {
  pet: Pet;
  onClose: () => void;
}

type TabType =
  | 'physical'
  | 'vaccinations'
  | 'grooming'
  | 'medical'
  | 'insurance'
  | 'documents';

export function HealthCareRecords({ pet, onClose }: HealthCareRecordsProps) {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('physical');

  const tabs = [
    {
      id: 'physical' as TabType,
      label: t('features.healthRecords.physicalMetrics'),
      icon: '📊',
    },
    {
      id: 'vaccinations' as TabType,
      label: t('features.healthRecords.vaccinations'),
      icon: '💉',
    },
    {
      id: 'grooming' as TabType,
      label: t('features.healthRecords.grooming'),
      icon: '✂️',
    },
    {
      id: 'medical' as TabType,
      label: t('features.healthRecords.medicalUpdates'),
      icon: '🏥',
    },
    {
      id: 'insurance' as TabType,
      label: t('features.healthRecords.insurance'),
      icon: '🛡️',
    },
    {
      id: 'documents' as TabType,
      label: t('features.healthRecords.documents'),
      icon: '📄',
    },
  ];

  const modalEl = (
    <div
      className="fixed inset-0 z-50 flex min-h-0 flex-col items-center justify-center overflow-hidden p-3 sm:p-4"
      style={{
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(6px)',
        WebkitBackdropFilter: 'blur(6px)',
      }}
    >
      {/* Card: always fits in viewport (maxHeight from padding); header + tabs fixed, details scroll */}
      <div
        className="flex min-h-0 w-full max-w-6xl flex-1 flex-col overflow-hidden rounded-xl border border-white/8 bg-white dark:border-white/8 dark:bg-[#16151D]"
        style={{
          boxShadow:
            '0 25px 50px -12px rgba(0,0,0,0.25), 0 12px 24px -8px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.08), 0 0 40px -10px rgba(99,102,241,0.2)',
          maxHeight: 'calc(100vh - 2rem)',
        }}
      >
        {/* Header – always visible */}
        <div className="flex-shrink-0 rounded-t-xl bg-white dark:bg-[#16151D]">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-purple-600 dark:from-blue-700 dark:to-purple-700 text-white p-4 sm:p-6 rounded-t-xl">
            <div className="flex justify-between items-start gap-3 mb-4">
              <div className="min-w-0">
                <h2
                  className="text-xl sm:text-2xl mb-1"
                  style={{ fontFamily: 'Sora, sans-serif', fontWeight: 700 }}
                >
                  {t('features.healthRecords.title')}
                </h2>
                <p
                  className="text-sm opacity-90"
                  style={{ fontFamily: 'Inter, sans-serif' }}
                >
                  {pet.name} - {pet.breed}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white/80 hover:text-white transition-colors flex-shrink-0 p-1 -m-1 text-2xl leading-none cursor-pointer"
                aria-label={t('common.close', 'Close')}
              >
                ×
              </button>
            </div>

            {/* Data Classification Notice */}
            <div className="bg-white/10 dark:bg-white/5 rounded-lg p-3 sm:p-4 border border-white/20 dark:border-white/10">
              <div className="grid md:grid-cols-2 gap-3 sm:gap-4">
                <div className="flex items-start gap-2">
                  <Lock className="w-4 h-4 mt-0.5" />
                  <div>
                    <div
                      className="text-sm opacity-90 mb-1"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('features.healthRecords.immutableLocked')}
                    </div>
                    <div
                      className="text-xs opacity-75"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.healthRecords.immutableFields')}
                    </div>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Edit className="w-4 h-4 mt-0.5" />
                  <div>
                    <div
                      className="text-sm opacity-90 mb-1"
                      style={{
                        fontFamily: 'Sora, sans-serif',
                        fontWeight: 600,
                      }}
                    >
                      {t('features.healthRecords.amendableContract')}
                    </div>
                    <div
                      className="text-xs opacity-75"
                      style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                      {t('features.healthRecords.amendableFields')}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs - horizontal scroll on mobile */}
          <div className="border-b border-gray-200 dark:border-white/8 bg-gray-50 dark:bg-[#0F0E16]">
            <div className="flex overflow-x-auto">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-1.5 sm:gap-2 px-4 py-3 sm:px-6 sm:py-4 whitespace-nowrap transition-colors cursor-pointer flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-white dark:bg-[#16151D] border-b-2 border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'text-gray-600 dark:text-white/70 hover:bg-gray-100 dark:hover:bg-white/5'
                  }`}
                  style={{
                    fontFamily: 'Inter, sans-serif',
                    fontWeight: activeTab === tab.id ? 600 : 400,
                  }}
                >
                  <span className="text-xl">{tab.icon}</span>
                  <span>{tab.label}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Details/updates section – only this area scrolls */}
        <div
          className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden bg-white p-4 dark:bg-[#16151D] sm:p-6"
          style={{ minHeight: 0 }}
        >
          {activeTab === 'physical' && <PhysicalMetrics petId={pet.id} />}
          {activeTab === 'vaccinations' && (
            <VaccinationRecords petId={pet.id} />
          )}
          {activeTab === 'grooming' && <GroomingRecords petId={pet.id} />}
          {activeTab === 'medical' && <MedicalUpdates petId={pet.id} />}
          {activeTab === 'insurance' && <InsuranceRecords petId={pet.id} />}
          {activeTab === 'documents' && <DocumentTimeline petId={pet.id} />}
        </div>
      </div>
    </div>
  );

  if (typeof document === 'undefined') return null;
  return createPortal(modalEl, document.body);
}
